import { A, G } from '@mobily/ts-belt';
import { Box, Carousel, Divider, ModuleHeader, Placeholder, Text } from '@stitch-fix/mode-react';
import ShoppableOutfit, { type ShoppableOutfitProps } from '@stitch-fix/mode-react/ShoppableOutfit';
import { InView, useInView } from 'react-intersection-observer';
import type { Merge } from 'type-fest';
import { SeeItOnMe } from '@stitch-fix/vision-shared';
import { useResultsMachine } from '../../utils/machines/results/context-provider';
import { trackEvent } from '../../utils/events';
import { OutfitsCarouselClick, OutfitsCarouselView } from '../../utils/events/types';
import { useCopy } from '../../utils/contexts/copy-provider';
import { formatFiltersVariable } from '../../utils/helpers';
import { useGetOutfitsResultsQuery, GetOutfitsResultsQueryVariables } from '../../utils/hooks/getOutfitsResults';
import { OutfitItemFragment, OutfitFragment } from '../../utils/hooks/getOutfitsBySku';
import { SearchParams, useSearchParams } from '../../utils/hooks/useSearchParams';
import { ModeStickerLabelTheme } from '../../types/definitions/vendor/graphql-schema.d';
type OutfitQueryParams = SearchParams & {
  outfitCacheId?: string | null;
};
type ImageComponent = ShoppableOutfitProps['imageComponents'][number];
type XAnchor = ImageComponent['laydown']['xAnchor'];
type YAnchor = ImageComponent['laydown']['yAnchor'];
type OutfitItemLabel = NonNullable<ImageComponent['label']>;
type ClarifiedOutfitItemFragment = Merge<OutfitItemFragment, {
  laydown: OutfitItemFragment['laydown'] & {
    xAnchor: XAnchor;
    yAnchor: YAnchor;
  };
}>;
type OutfitItemEventData = Merge<OutfitItemFragment, {
  id: string;
}>;
const themeMapping: Record<ModeStickerLabelTheme, OutfitItemLabel['theme']> = {
  NAVY: 'navy',
  WHITE: 'white',
  CITRUS: 'citrus',
  MINT: 'mint',
  // TODO: Update this when the correct theme enums are available: https://github.com/stitchfix/web-frontend/blob/main/packages/mode-react/src/ShoppableOutfit/types.ts#L21
  DEFAULT_INVERSE: 'navy',
  DEFAULT_LIGHT: 'white'
};

/**
 * Function that formats and reformats the query, color, and occasion to send to the graph
 */
const formatQueryVariables = ({
  color = [],
  occasion = [],
  q = '',
  outfitCacheId
}: OutfitQueryParams) => {
  const variables: GetOutfitsResultsQueryVariables = {
    q,
    outfitCacheId
  };
  const filters = formatFiltersVariable({
    color,
    occasion
  });
  if (G.isArray(filters)) {
    variables.filters = filters;
  }
  return variables;
};

/**
 * Function that transforms Outfit Items into Image Components for the carousel to render
 */
const transformOutfitItemToImageComponent = (item: ClarifiedOutfitItemFragment): Omit<ImageComponent, 'url'> => ({
  id: item.shopProductVariant?.sku ?? '',
  imageUrl: item.shopProductVariant?.image?.imageUrl ?? '',
  altText: item.shopProductVariant?.image?.altText ?? '',
  label: item.sticker ? {
    text: item.sticker.label,
    theme: themeMapping[item.sticker.theme]
  } : undefined,
  laydown: {
    ...item.laydown,
    xAnchor: item.laydown.xAnchor,
    yAnchor: item.laydown.yAnchor
  }
});

/**
 * Guard Type that checks if it is a ClarifiedOutfitItem
 */
const isClarifiedOutfitItemFragment = (outfitItem: OutfitItemFragment): outfitItem is ClarifiedOutfitItemFragment => {
  const xAnchor: XAnchor[] = ['left', 'right', 'center'];
  const yAnchor: YAnchor[] = ['top', 'bottom', 'center'];
  return A.includes(xAnchor, outfitItem.laydown.xAnchor) && A.includes(yAnchor, outfitItem.laydown.yAnchor);
};

/**
 * Loading Slides for the carousel's loading state.
 */
const loadingSlides = Array.from({
  length: 5
}, (_, key) => <Placeholder data-testid="loading-placeholder" variant="rect" width="100%" height={0} pt="100%" key={key} />);
const headerLoadingState = <Text color="gray-16" mb={{
  sm: 1,
  md: 1.5
}} setting="title-medium">
    <Placeholder width={10} />
  </Text>;

/**
 * Tracking outfit carousel view event
 */
const onOutfitVisible = (outfitId: string, queryId: string) => {
  const viewEvent: OutfitsCarouselView = {
    action_detail: 'Search',
    action_name: 'search_outfit_impression_view',
    action_type: 'view',
    canonical_name: 'search_results',
    event_name: 'search_results',
    additional_context: {
      direct_buy: {
        outfit_id: outfitId,
        query_id: queryId
      }
    }
  };
  trackEvent(viewEvent);
};

/**
 * Tracking Outfit Click Event
 */
const onOutfitItemClick = (outfitId: string, queryId: string, e: OutfitItemEventData) => {
  const clickEvent: OutfitsCarouselClick = {
    action_detail: 'Search',
    action_name: 'search_outfit_item_selected',
    action_type: 'click',
    canonical_name: 'search_results',
    event_name: 'search_results',
    additional_context: {
      direct_buy: {
        outfit_id: outfitId,
        query_id: queryId,
        sku_id: e.id,
        sub_source: 'search-outfits'
      }
    }
  };
  trackEvent(clickEvent);
};

/**
 * A function creates an array of outfit elements based on the data that comes from the server
 */
const getOutfitSlides = (queryId: string, outfits: OutfitFragment[], carouselInView: boolean) => outfits.map((outfit, index) => {
  const imageComponents = outfit.items.filter(isClarifiedOutfitItemFragment).map(item => {
    const baseUrl = item?.shopProductVariant?.url ? item.shopProductVariant.url.replace('https://www.stitchfix.com', '') : '';
    const url = baseUrl ? `${baseUrl}&outfit_id=${outfit.id}&source=search_results&query_id=${queryId}` : '';
    return {
      ...transformOutfitItemToImageComponent(item),
      url
    };
  });
  return <InView key={outfit.id} triggerOnce onChange={inView => inView && onOutfitVisible(outfit.id, queryId)} data-testid="slide-observer-wrapper">
        <ShoppableOutfit key={outfit.id} imageComponents={imageComponents || []} dotRerenderCount={carouselInView ? 1 : 0} onOutfitItemClick={e => onOutfitItemClick(outfit.id, queryId, e)} outfitId={outfit.id} eventOverrides={{
      view: {
        order: {
          index
        },
        collection_length: outfits.length
      }
    }} topLeftContent={<SeeItOnMe skuIds={outfit.items.map(item => item.shopProductVariant?.sku ?? '')} eventData={{
      sourceApp: 'categories-next-ui'
    }} />} />
      </InView>;
});

/**
 * The component handles rendering outfits results
 */
export const ResultsOutfits = () => {
  const {
    copy
  } = useCopy();
  const {
    queryParams
  } = useSearchParams();
  const {
    state
  } = useResultsMachine();
  const queryId = state.matches('success') ? state.context.data.queryId : '';
  const outfitCacheId = state.matches('success') ? state.context.data.outfitCacheId : null;
  const shouldSkipOutfitsQuery = G.isNull(outfitCacheId) || queryParams.page !== 1;
  const {
    data,
    loading,
    error
  } = useGetOutfitsResultsQuery({
    variables: formatQueryVariables({
      ...queryParams,
      outfitCacheId
    }),
    skip: shouldSkipOutfitsQuery
  });
  const [carouselRef, carouselInView] = useInView({
    threshold: 0.5,
    triggerOnce: true
  });
  const outfits = data?.shopContent?.search?.outfits.edges.map(({
    node
  }) => node) || [];
  if (error || shouldSkipOutfitsQuery || !loading && A.isEmpty(outfits)) {
    return null;
  }
  return <Box>
      <Divider variant="subtle" my={{
      sm: 1.5,
      md: 3
    }} />
      {loading ? headerLoadingState : <ModuleHeader mb="default" header={copy.outfitsCarouselTitle} />}
      <Box ref={carouselRef}>
        <Carousel ariaLabel={copy.outfitsCarouselTitle} gutterOverflow={{
        sm: 1,
        md: 1.5,
        lg: 0
      }} slideWidth={{
        sm: '320px',
        md: '384px'
      }}>
          {loading ? loadingSlides : getOutfitSlides(queryId, outfits, carouselInView)}
        </Carousel>
      </Box>
      <Divider variant="subtle" my={{
      sm: 1.5,
      md: 3
    }} />
    </Box>;
};